import { CONTACT_EMAIL, PRIVACY_EMAIL } from '@smbsuite/legal';
import type { Language } from '../../i18n';

/**
 * Who owns DoCuLat and answers for its data, in one place: the footer, the privacy policy, the data
 * processing agreement and the copyright notice all read it from here (TF-13, TF-255). The contact
 * address must be a mailbox somebody reads before the pages go to production.
 */
export const LEGAL = {
  /** Who holds the rights in the software (the copyright notice and the footer). */
  holder: 'Ivars Miezis',
  firstYear: 2026,
  /**
   * The company that provides the service and signs the agreements (TF-255). A placeholder until the
   * company is named: legal.test.ts fails a production build with registration open while any of
   * these is still "TBD".
   */
  provider: 'TBD SIA',
  registrationNumber: 'TBD',
  address: 'TBD, Latvia',
  contactEmail: PRIVACY_EMAIL,
  /** The single point of contact (the Digital Services Act, TF-256): see CONTACT_EMAIL. */
  contactPoint: CONTACT_EMAIL,
  /** When the texts below last changed; shown on every legal page. */
  updated: '2026-10-19',
  /**
   * The last change people are told of in the app (a banner until they close it): set it to `updated`
   * when a change matters to them, leave it when it only fixes wording.
   */
  announced: '2026-10-13',
} as const;

/** The provider fields still to be filled in, by name. */
export function placeholders(legal: Record<string, unknown> = LEGAL): string[] {
  return ['provider', 'registrationNumber', 'address'].filter((key) => String(legal[key] ?? '').includes('TBD'));
}

/** "TBD SIA, registration number TBD, TBD, Latvia": how the agreements name the provider. */
export const providerLine = (registration: string) => `${LEGAL.provider}, ${registration} ${LEGAL.registrationNumber}, ${LEGAL.address}`;

/** "2026", or "2026-2028" once the years go by. */
export function copyrightYears(now = new Date()): string {
  const year = now.getFullYear();
  return year > LEGAL.firstYear ? `${LEGAL.firstYear}–${year}` : String(LEGAL.firstYear);
}

/**
 * A legal text as data, so a page renders any of them and a new one (terms of service, a data
 * processing agreement) is a new file of content rather than a new page.
 */
export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
  /** A table: the roles of each kind of person, the sub-processors. The first column names the row. */
  table?: { head: string[]; rows: string[][] };
  /** Links to the other legal pages, after the text. */
  links?: { label: string; to: string }[];
}

export interface LegalDocument {
  title: string;
  intro: string;
  sections: LegalSection[];
}

/** English always (the text a language without its own falls back to), and any language's own. */
export type LegalTexts = { en: LegalDocument } & Partial<Record<Language, LegalDocument>>;
